import React from 'react';
import { Input } from '../ui/input';
import { TooltipLabel } from '../ui/TooltipLabel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { costManagementTooltips } from '../../utils/strategicFrameworkTooltips';
import { getCurrencySymbol, CURRENCIES } from '../../constants/currencies';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useBusinessPlan } from '../../App';
import StrategicFrameworkLinker from './StrategicFrameworkLinker';
import PerYearGrowthTable from './PerYearGrowthTable';
import ProductMetricsDisplay from './ProductMetricsDisplay';
import { 
  SemiVariableFields, 
  DirectCostFields, 
  IndirectCostFields, 
  OpExFields, 
  CapExFields, 
  FinancialCostFields, 
  TaxFields 
} from './TypeSpecificFields';

/**
 * Complete cost form with all fields based on category
 */
const CostFormContent = ({ 
  form, 
  setForm, 
  category, 
  costLibraries, 
  sfHierarchy, 
  financialLoans, 
  products 
}) => {
  const [isCustomMode, setIsCustomMode] = React.useState(false);
  const [customItemName, setCustomItemName] = React.useState('');
  const { displayCurrency } = useCurrency();
  const { currentPlan } = useBusinessPlan();

  // Dynamic unit label based on cost type - now with currency
  const getUnitLabel = () => {
    const currency = form.currency || currentPlan?.currency || 'USD';
    const symbol = getCurrencySymbol(currency);
    const labels = {
      'monthly': `Monthly Amount (${symbol})`,
      'annual': `Annual Amount (${symbol})`,
      'one_time': `One-Time Amount (${symbol})`,
      'percentage_of_revenue': 'Percentage of Revenue (%)',
      'per_unit': `Cost per Unit (${symbol})`,
      'per_employee': `Cost per Employee (${symbol})`,
      'per_square_foot': `Cost per Square Foot (${symbol})`
    };
    return labels[form.cost_type] || `Amount (${symbol})`;
  };

  const handleItemSelection = (itemName) => {
    if (itemName === 'Custom') {
      setIsCustomMode(true);
      setCustomItemName('');
      setForm({...form, item_name: ''});
    } else {
      setIsCustomMode(false);
      setForm({...form, item_name: itemName});
      
      // Auto-fill typical values
      const library = costLibraries[category] || [];
      const item = library.find(i => i.name === itemName);
      if (item) {
        setForm(prev => ({
          ...prev,
          item_name: itemName,
          cost_type: item.typical_cost_type || prev.cost_type
        }));
      }
    }
  };

  const handleCustomNameChange = (value) => {
    setCustomItemName(value);
    setForm({...form, item_name: value});
  };

  return (
    <div className="space-y-6">
      {/* Cost Item Selection */}
      <div className="space-y-2">
        <TooltipLabel 
          htmlFor="item-name" 
          tooltip={costManagementTooltips[category + 'Costs']?.itemName || 'Select or enter cost item name'}
        >
          Cost Item Name *
        </TooltipLabel>
        <Select value={isCustomMode ? 'Custom' : form.item_name} onValueChange={handleItemSelection}>
          <SelectTrigger>
            <SelectValue placeholder="Select cost item" />
          </SelectTrigger>
          <SelectContent>
            {(costLibraries[category] || []).map((item, index) => (
              <SelectItem key={index} value={item.name}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {isCustomMode && (
          <Input
            placeholder="Enter custom cost item name"
            value={customItemName}
            onChange={(e) => handleCustomNameChange(e.target.value)}
            className="mt-2"
            autoFocus
          />
        )}
      </div>

      {/* Description */}
      <div className="space-y-2">
        <TooltipLabel htmlFor="description" tooltip="Additional details about this cost">
          Description (Optional)
        </TooltipLabel>
        <Textarea
          id="description"
          value={form.description}
          onChange={(e) => setForm({...form, description: e.target.value})}
          placeholder="Provide additional details..."
          rows={2}
        />
      </div>

      {/* Amount, Currency, and Cost Type */}
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <TooltipLabel htmlFor="amount" tooltip="Primary cost amount">
            {getUnitLabel()}
          </TooltipLabel>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
              {getCurrencySymbol(form.currency || currentPlan?.currency || 'USD')}
            </span>
            <Input
              id="amount"
              type="number"
              value={form.amount}
              onChange={(e) => setForm({...form, amount: e.target.value})}
              placeholder={form.cost_type === 'percentage_of_revenue' ? 'e.g., 15' : 'e.g., 5000'}
              className="pl-8"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <TooltipLabel htmlFor="currency" tooltip="Currency for this cost amount">
            Currency *
          </TooltipLabel>
          <Select 
            value={form.currency || currentPlan?.currency || 'USD'} 
            onValueChange={(val) => setForm({...form, currency: val})}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((curr) => (
                <SelectItem key={curr.value} value={curr.value}>
                  {getCurrencySymbol(curr.value)} {curr.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <TooltipLabel htmlFor="cost-type" tooltip="How this cost is calculated">
            Cost Type *
          </TooltipLabel>
          <Select value={form.cost_type} onValueChange={(val) => setForm({...form, cost_type: val})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly Recurring</SelectItem>
              <SelectItem value="annual">Annual Recurring</SelectItem>
              <SelectItem value="one_time">One-Time Cost</SelectItem>
              <SelectItem value="per_unit">Per Unit Produced</SelectItem>
              <SelectItem value="percentage_of_revenue">Percentage of Revenue</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Currency conversion notice */}
      {form.currency && displayCurrency && form.currency !== displayCurrency && (
        <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
          ℹ️ This cost is in {form.currency}, will display as {displayCurrency} when viewing
        </div>
      )}

      {/* Type-Specific Fields */}
      {category === 'semi_variable' && <SemiVariableFields form={form} setForm={setForm} />}
      {category === 'direct' && <DirectCostFields form={form} setForm={setForm} products={products} />}
      {category === 'indirect' && <IndirectCostFields form={form} setForm={setForm} products={products} />}
      {category === 'opex' && <OpExFields form={form} setForm={setForm} />}
      {category === 'capex' && <CapExFields form={form} setForm={setForm} />}
      {category === 'financial' && <FinancialCostFields form={form} setForm={setForm} financialLoans={financialLoans} />}
      {category === 'taxes' && <TaxFields form={form} setForm={setForm} />}

      {/* Strategic Framework Linking */}
      <StrategicFrameworkLinker 
        form={form} 
        setForm={setForm} 
        sfHierarchy={sfHierarchy} 
      />

      {/* Product Performance Metrics for Dynamic Costs */}
      {(form.cost_type === 'per_unit' || form.cost_type === 'percentage_of_revenue') && form.linked_product_id && (
        <ProductMetricsDisplay
          linkedProductId={form.linked_product_id}
          costType={form.cost_type}
          costAmount={form.amount}
          currency={form.currency || currentPlan?.currency || 'USD'}
          mode="planning"
          onCalculatedAmountChange={(calculatedAmount) => {
            // Optionally update form with calculated amount
            console.log('Calculated planned cost amount:', calculatedAmount);
          }}
        />
      )}

      {/* Per-Year Growth Rates */}
      <PerYearGrowthTable form={form} setForm={setForm} />

      {/* Notes */}
      <div className="space-y-2">
        <TooltipLabel htmlFor="notes" tooltip="Additional notes or comments">
          Notes (Optional)
        </TooltipLabel>
        <Textarea
          id="notes"
          value={form.notes}
          onChange={(e) => setForm({...form, notes: e.target.value})}
          placeholder="Any additional notes or comments..."
          rows={2}
        />
      </div>
    </div>
  );
};

export default CostFormContent;
